/**
 * Accounting Dashboard Page
 *
 * Compact dashboard with:
 * 1. Fast actions row (buttons with icons)
 * 2. New items by section (like "Oggi" view)
 * 3. Monthly totals and pending amounts
 */

import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CentsAmount } from '@business-assistant/shared';
import { useAccountingStore } from '../../state/accountingState';
import '../accounting.css';

// =============================================================================
// TYPES
// =============================================================================

interface AccountingDashboardProps {
  onNavigate?: (section: string) => void;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatCurrency(cents: CentsAmount): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
  });
}

function isRecent(dateStr: string, days: number = 7): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays <= days;
}

// =============================================================================
// FAST ACTION BUTTON
// =============================================================================

interface FastActionProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function FastAction({ icon, label, onClick }: FastActionProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1rem',
        background: 'var(--bg-elevated, white)',
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: '0.5rem',
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: 'var(--text-primary, #111827)',
        cursor: 'pointer',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--bg-hover, #f3f4f6)';
        e.currentTarget.style.borderColor = 'var(--brand, #3b82f6)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--bg-elevated, white)';
        e.currentTarget.style.borderColor = 'var(--border, #e5e7eb)';
      }}
    >
      {icon}
      {label}
    </button>
  );
}

// =============================================================================
// SECTION LIST ITEM
// =============================================================================

interface ListItemProps {
  title: string;
  subtitle?: string;
  amount?: CentsAmount;
  date?: string;
  direction?: 'in' | 'out';
  onClick?: () => void;
}

function ListItem({ title, subtitle, amount, date, direction, onClick }: ListItemProps) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 0',
        borderBottom: '1px solid var(--border-light, #f3f4f6)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'var(--text-primary, #111827)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--text-tertiary, #9ca3af)',
            marginTop: '0.125rem',
          }}>
            {subtitle}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {amount !== undefined && (
          <span style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: direction === 'in'
              ? 'var(--success, #10b981)'
              : direction === 'out'
                ? 'var(--danger, #ef4444)'
                : 'var(--text-primary, #111827)',
          }}>
            {direction === 'in' ? '+' : direction === 'out' ? '-' : ''}
            {formatCurrency(Math.abs(amount))}
          </span>
        )}
        {date && (
          <span style={{
            fontSize: '0.75rem',
            color: 'var(--text-tertiary, #9ca3af)',
          }}>
            {formatDate(date)}
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STAT CARD
// =============================================================================

interface StatCardProps {
  label: string;
  value: string;
  subtext?: string;
  color?: 'default' | 'success' | 'danger' | 'warning';
  onClick?: () => void;
}

function StatCard({ label, value, subtext, color = 'default', onClick }: StatCardProps) {
  const colorMap = {
    default: 'var(--text-primary, #111827)',
    success: 'var(--success, #10b981)',
    danger: 'var(--danger, #ef4444)',
    warning: 'var(--warning, #f59e0b)',
  };

  return (
    <div
      onClick={onClick}
      style={{
        flex: 1,
        padding: '1rem',
        background: 'var(--bg-elevated, white)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border, #e5e7eb)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{
        fontSize: '0.75rem',
        fontWeight: 500,
        color: 'var(--text-secondary, #6b7280)',
        textTransform: 'uppercase',
        letterSpacing: '0.025em',
        marginBottom: '0.375rem',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '1.5rem',
        fontWeight: 700,
        color: colorMap[color],
        lineHeight: 1.2,
      }}>
        {value}
      </div>
      {subtext && (
        <div style={{
          fontSize: '0.75rem',
          color: 'var(--text-tertiary, #9ca3af)',
          marginTop: '0.25rem',
        }}>
          {subtext}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function AccountingDashboard({ onNavigate }: AccountingDashboardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'it' ? 'it-IT' : 'en-US';

  const {
    invoices,
    movements,
    movementStats,
    loading,
    loadInvoices,
    loadMovements,
    loadInvoiceStats,
    loadMovementStats,
  } = useAccountingStore();

  useEffect(() => {
    loadInvoices();
    loadMovements();
    loadInvoiceStats();
    loadMovementStats();
  }, [loadInvoices, loadMovements, loadInvoiceStats, loadMovementStats]);

  // Calculate data
  const data = useMemo(() => {
    const now = new Date().toISOString();
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    // Recent invoices (last 7 days)
    const recentInvoices = invoices
      .filter(inv => isRecent(inv.createdAt || inv.issueDate))
      .slice(0, 5);

    // Recent movements (last 7 days)
    const recentMovements = movements
      .filter(mov => isRecent(mov.createdAt || mov.date))
      .slice(0, 5);

    // This month totals
    const thisMonthMovements = movements.filter(mov => {
      const date = new Date(mov.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
    const monthlyIn = thisMonthMovements
      .filter(m => m.direction === 'in')
      .reduce((sum, m) => sum + m.amount, 0);
    const monthlyOut = thisMonthMovements
      .filter(m => m.direction === 'out')
      .reduce((sum, m) => sum + m.amount, 0);

    // Pending invoices to collect (issued, not paid)
    const issuedInvoices = invoices.filter(inv =>
      (inv as any).direction !== 'received' &&
      inv.status !== 'paid' &&
      inv.status !== 'cancelled'
    );
    const toCollect = issuedInvoices.reduce(
      (sum, inv) => sum + (inv.grossTotal - inv.paidAmount), 0
    );
    const overdueToCollect = issuedInvoices.filter(inv => inv.dueDate < now);

    // Pending invoices to pay (received, not paid)
    const receivedInvoices = invoices.filter(inv =>
      (inv as any).direction === 'received' &&
      inv.status !== 'paid' &&
      inv.status !== 'cancelled'
    );
    const toPay = receivedInvoices.reduce(
      (sum, inv) => sum + (inv.grossTotal - inv.paidAmount), 0
    );
    const overdueToPay = receivedInvoices.filter(inv => inv.dueDate < now);

    return {
      recentInvoices,
      recentMovements,
      monthlyIn,
      monthlyOut,
      monthlyBalance: monthlyIn - monthlyOut,
      toCollect,
      toCollectOverdueCount: overdueToCollect.length,
      toPay,
      toPayOverdueCount: overdueToPay.length,
      totalBalance: (movementStats?.totalIn ?? 0) - (movementStats?.totalOut ?? 0),
    };
  }, [invoices, movements, movementStats]);

  const handleNavigate = (section: string) => {
    onNavigate?.(section);
  };

  // Icons
  const icons = {
    invoice: (
      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    movement: (
      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
    customer: (
      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
      </svg>
    ),
    import: (
      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
    ),
  };

  return (
    <div style={{ padding: '1rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Loading */}
      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('accounting.dashboard.loading')}
        </div>
      )}

      {/* 1. FAST ACTIONS ROW */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginBottom: '1.5rem',
      }}>
        <FastAction
          icon={icons.invoice}
          label={t('accounting.dashboard.newInvoice')}
          onClick={() => handleNavigate('invoices/new')}
        />
        <FastAction
          icon={icons.movement}
          label={t('accounting.dashboard.newMovement')}
          onClick={() => handleNavigate('movements/new')}
        />
        <FastAction
          icon={icons.customer}
          label={t('accounting.dashboard.newCustomer')}
          onClick={() => handleNavigate('customers/new')}
        />
        <FastAction
          icon={icons.import}
          label={t('accounting.dashboard.importStatement')}
          onClick={() => handleNavigate('banking/import')}
        />
      </div>

      {/* 2. NEW ITEMS BY SECTION */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
      }}>
        {/* Recent Invoices */}
        <div style={{
          background: 'var(--bg-elevated, white)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border, #e5e7eb)',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.875rem 1rem',
            borderBottom: '1px solid var(--border, #e5e7eb)',
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--text-primary, #111827)',
            }}>
              {t('accounting.dashboard.recentInvoices')}
            </h3>
            <button
              onClick={() => handleNavigate('invoices')}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '0.75rem',
                color: 'var(--brand, #3b82f6)',
                cursor: 'pointer',
              }}
            >
              {t('accounting.dashboard.viewAll')}
            </button>
          </div>
          <div style={{ padding: '0 1rem' }}>
            {data.recentInvoices.length > 0 ? (
              data.recentInvoices.map((inv) => (
                <ListItem
                  key={inv.id}
                  title={inv.customerName}
                  subtitle={`#${inv.id.slice(-6)}`}
                  amount={inv.grossTotal}
                  date={inv.issueDate}
                  onClick={() => handleNavigate(`invoices/${inv.id}`)}
                />
              ))
            ) : (
              <div style={{
                padding: '2rem 0',
                textAlign: 'center',
                color: 'var(--text-tertiary, #9ca3af)',
                fontSize: '0.875rem',
              }}>
                {t('accounting.dashboard.noRecentInvoices')}
              </div>
            )}
          </div>
        </div>

        {/* Recent Movements */}
        <div style={{
          background: 'var(--bg-elevated, white)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border, #e5e7eb)',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.875rem 1rem',
            borderBottom: '1px solid var(--border, #e5e7eb)',
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--text-primary, #111827)',
            }}>
              {t('accounting.dashboard.recentMovements')}
            </h3>
            <button
              onClick={() => handleNavigate('movements')}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '0.75rem',
                color: 'var(--brand, #3b82f6)',
                cursor: 'pointer',
              }}
            >
              {t('accounting.dashboard.viewAll')}
            </button>
          </div>
          <div style={{ padding: '0 1rem' }}>
            {data.recentMovements.length > 0 ? (
              data.recentMovements.map((mov) => (
                <ListItem
                  key={mov.id}
                  title={mov.description}
                  subtitle={mov.category}
                  amount={mov.amount}
                  date={mov.date}
                  direction={mov.direction}
                  onClick={() => handleNavigate(`movements/${mov.id}`)}
                />
              ))
            ) : (
              <div style={{
                padding: '2rem 0',
                textAlign: 'center',
                color: 'var(--text-tertiary, #9ca3af)',
                fontSize: '0.875rem',
              }}>
                {t('accounting.dashboard.noRecentMovements')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3. TOTALS - First row: monthly stats */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '1rem',
      }}>
        <StatCard
          label={t('accounting.dashboard.monthlyIncome')}
          value={formatCurrency(data.monthlyIn)}
          color="success"
          onClick={() => handleNavigate('movements')}
        />
        <StatCard
          label={t('accounting.dashboard.monthlyExpenses')}
          value={formatCurrency(data.monthlyOut)}
          color="danger"
          onClick={() => handleNavigate('movements')}
        />
        <StatCard
          label={t('accounting.dashboard.currentBalance')}
          value={formatCurrency(data.totalBalance)}
          color={data.totalBalance >= 0 ? 'success' : 'danger'}
        />
      </div>

      {/* Second row: to collect / to pay (50/50) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem',
      }}>
        <StatCard
          label={t('accounting.dashboard.toReceive')}
          value={formatCurrency(data.toCollect)}
          subtext={data.toCollectOverdueCount > 0 ? `${data.toCollectOverdueCount} scadute` : undefined}
          color={data.toCollectOverdueCount > 0 ? 'warning' : 'default'}
          onClick={() => handleNavigate('duedates')}
        />
        <StatCard
          label={t('accounting.dashboard.toPay')}
          value={formatCurrency(data.toPay)}
          subtext={data.toPayOverdueCount > 0 ? `${data.toPayOverdueCount} scadute` : undefined}
          color={data.toPayOverdueCount > 0 ? 'danger' : 'default'}
          onClick={() => handleNavigate('duedates')}
        />
      </div>
    </div>
  );
}

export type { AccountingDashboardProps };
